import React, { useEffect, useState } from "react";
import { api } from "@/api";
import { Printer, ScrollText, FileText, Shield, Gavel, Handshake, FileSignature } from "lucide-react";

export const LEGAL_ICONS = { mentions_legales: Gavel, cgv: FileText, cgu: ScrollText, confidentialite: Shield, bp_rules: Handshake, bp_contract: FileSignature };
export const LEGAL_SHORT = { mentions_legales: "Mentions légales", cgv: "CGV", cgu: "CGU", confidentialite: "Confidentialité", bp_rules: "Règlement BP", bp_contract: "Contrat BP" };

export function fdate(iso) {
  return iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }) : "—";
}

export function LegalHtml({ html, testid = "legal-html" }) {
  return <article className="legal-prose" data-testid={testid} dangerouslySetInnerHTML={{ __html: html || "" }} />;
}

/** Onglets Mentions légales / CGV / CGU / Confidentialité — lecture (public, chef, salarié). */
export function LegalTabs({ current, onChange, status = {}, renderExtra }) {
  const [list, setList] = useState([]);
  const [doc, setDoc] = useState(null);

  useEffect(() => { api.get("/public/legal").then((r) => setList(r.data || [])).catch(() => setList([])); }, []);
  useEffect(() => {
    if (!current) return;
    setDoc(null);
    api.get(`/public/legal/${current}`).then((r) => setDoc(r.data)).catch(() => setDoc({ error: true }));
  }, [current]);
  // Le règlement Business Partner n'apparaît que pour les BP (acceptation requise) ou quand il est explicitement ouvert.
  // Le modèle de contrat BP (non listé par l'API, signature réservée aux BP connectés) s'ajoute quand il est ouvert via /legal/bp_contract.
  const visible = list.filter((d) => d.key !== "bp_rules" || current === "bp_rules" || status[d.key]?.acceptance_required);
  if (current === "bp_contract" && !visible.some((d) => d.key === "bp_contract")) visible.push({ key: "bp_contract", title: doc?.title || "Contrat Business Partner (modèle)" });

  return (
    <div className="grid lg:grid-cols-[240px_1fr] gap-6" data-testid="legal-tabs">
      <nav className="flex lg:flex-col gap-1 overflow-x-auto" data-testid="legal-tablist">
        {visible.map((d) => {
          const Icon = LEGAL_ICONS[d.key] || FileText;
          const active = d.key === current;
          const pending = status[d.key]?.needs_acceptance;
          return (
            <button type="button" key={d.key} onClick={() => onChange(d.key)} data-testid={`legal-tab-${d.key}`} data-active={active ? "1" : "0"}
              className={`text-left px-3 py-2.5 text-sm font-semibold border flex items-center gap-2 whitespace-nowrap lg:whitespace-normal transition-colors ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white border-[#DEE2E6] hover:border-[#0A0A0A]"}`}>
              <Icon className="h-4 w-4 shrink-0" />
              <span className="flex-1">{d.title}</span>
              {pending && <span className="w-2 h-2 bg-[#E60000] rounded-full shrink-0" data-testid={`legal-tab-pending-${d.key}`} />}
            </button>
          );
        })}
      </nav>
      <div className="bg-white border border-[#DEE2E6] min-h-[400px]" data-testid="legal-doc">
        {!doc ? <div className="p-10 text-sm text-[#868E96]" data-testid="legal-doc-loading">Chargement…</div> : doc.error ? <div className="p-10 text-sm text-[#E60000]">Document indisponible.</div> : (
          <>
            <div className="px-6 lg:px-10 py-4 border-b border-[#DEE2E6] bg-[#F8F9FA] flex flex-wrap items-center gap-3 text-xs text-[#495057]">
              <span className="font-mono font-bold uppercase tracking-wider" data-testid="legal-doc-version">Version {doc.version}</span>
              <span>· publiée le {fdate(doc.published_at)}</span>
              <span className="flex-1" />
              <button type="button" onClick={() => window.print()} data-testid="legal-print" className="inline-flex items-center gap-1 font-semibold hover:text-[#002FA7]"><Printer className="h-3.5 w-3.5" />Imprimer</button>
            </div>
            {renderExtra && renderExtra(doc)}
            <div className="px-6 lg:px-10 py-8"><LegalHtml html={doc.html} /></div>
          </>
        )}
      </div>
    </div>
  );
}
